import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { TableHandles } from "models/propTypes/simpleTableProps";
import { IconDefinitionConstants, ReportConstants } from "utils/constants";
//#endregion
//#region components
import { DashboardButtonGroup, FilterButton, PageLayouts } from "components/common";
import { Grid } from "@mui/material";
import useCommonFilterProps from "./forms/useCommonFilterProps";
import useHoldingRegisterReportFilterDefinition from "./forms/useReportFilterDefinition";
import HoldingRegistersList from "./holdingRegistersList";
import { useHoldingRegisterService } from "serviceHooks";
import useHoldingRegisterPermissions from "./usePermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const HoldingRegisterReportView: React.FC = () => {
  const holdingRegisterService = useHoldingRegisterService();
  const { filterFormFields, filterQueryStateHandler } = useHoldingRegisterReportFilterDefinition();
  const { initialFilter } = useCommonFilterProps();

  const { hasHoldingRegisterAvailable } = useHoldingRegisterPermissions();

  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.HoldingRegister.HoldingRegister);
  const fileName = `${title} - ${companyName}`;

  const tableRef = React.useRef<TableHandles>(null);

  const exportReportExcel = async () => {
    tableRef.current?.exportToExcel();
  };

  const exportReportCSV = async () => {
    tableRef.current?.exportToCSV();
  };

  const exportReportPDF = async () => {
    tableRef.current?.exportToPDF();
  };

  return (
    <PageLayouts.DashboardLayout
      key={ReportConstants.ReportKeys.HoldingRegisters}
      entityKey={0}
      title={title}
      hasPermissions={hasHoldingRegisterAvailable}
      topRightButtons={
        <DashboardButtonGroup
          buttons={[<FilterButton />]}
          moreActions={[
            {
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToExcel),
              icon: IconDefinitionConstants.WSave,
              onClickHandler: exportReportExcel,
            },
            {
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
              icon: IconDefinitionConstants.WSave,
              onClickHandler: exportReportCSV,
            },
            {
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DownloadAsPDF),
              icon: IconDefinitionConstants.WDownload,
              onClickHandler: exportReportPDF,
            },
          ]}
        />
      }
      filterFields={filterFormFields}
      initialFilter={initialFilter}
      reportToAPIFilterFormatter={holdingRegisterService.reportToAPIFilter}
      filterQueryStateHandler={filterQueryStateHandler}
    >
      <Grid item container>
        <HoldingRegistersList
          ref={tableRef}
          exportFileName={fileName}
          exportFileDescription={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.HoldingRegister.HoldingRegisterDescription
          )}
        />
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default HoldingRegisterReportView;
